import { useMemo } from "react"
import { type OperationContext, type OperationResult, useMutation, useQuery } from "urql"

import {
  AddCompanyRegistrationDocument,
  DeleteCompanyRegistrationDocument,
  RegistrationListDocument,
  UpdateCompanyRegistrationDocument,
} from "@/graphql/codegen/graphql"
import type {
  CompanyRegistration,
  CompanyRegistrationMutationCreateArgs,
  CompanyRegistrationMutationDeleteArgs,
  CompanyRegistrationMutationUpdateArgs,
  Mutation,
  Query,
} from "@/graphql/codegen/graphql"

interface UseRegistrationProps {
  companyId?: string
  pause?: boolean
}

const useCompanyRegistration = ({ companyId = "", pause = false }: UseRegistrationProps = {}): {
  registrationList: CompanyRegistration[]
  registrationListFetching: boolean
  refetchRegistrationList: () => void
  addRegistration: (
    variables: CompanyRegistrationMutationCreateArgs,
    context?: Partial<OperationContext>
  ) => Promise<OperationResult<Pick<Mutation, "companyRegistration">, CompanyRegistrationMutationCreateArgs>>
  updateRegistration: (
    variables: CompanyRegistrationMutationUpdateArgs,
    context?: Partial<OperationContext>
  ) => Promise<OperationResult<Pick<Mutation, "companyRegistration">, CompanyRegistrationMutationUpdateArgs>>
  deleteRegistration: (
    variables: CompanyRegistrationMutationDeleteArgs,
    context?: Partial<OperationContext>
  ) => Promise<OperationResult<Pick<Mutation, "companyRegistration">, CompanyRegistrationMutationDeleteArgs>>
} => {
  const [{ data, fetching: registrationListFetching }, executeQuery] = useQuery<Pick<Query, "companyRegistration">>({
    query: RegistrationListDocument,
    variables: { input: { companyId } },
    pause: pause || !companyId,
  })

  const refetchRegistrationList = () => {
    executeQuery({ requestPolicy: "network-only" })
  }

  const registrationList = useMemo(() => {
    if (!data?.companyRegistration?.list) {
      return []
    }
    return data.companyRegistration.list.__typename === "CompanyRegistrationListSuccess"
      ? (data.companyRegistration.list.companyRegistration ?? [])
      : []
  }, [data]) as CompanyRegistration[]

  const [, addRegistration] = useMutation<Pick<Mutation, "companyRegistration">, CompanyRegistrationMutationCreateArgs>(
    AddCompanyRegistrationDocument
  )

  const [, updateRegistration] = useMutation<
    Pick<Mutation, "companyRegistration">,
    CompanyRegistrationMutationUpdateArgs
  >(UpdateCompanyRegistrationDocument)

  const [, deleteRegistration] = useMutation<
    Pick<Mutation, "companyRegistration">,
    CompanyRegistrationMutationDeleteArgs
  >(DeleteCompanyRegistrationDocument)

  return {
    registrationList,
    registrationListFetching,
    refetchRegistrationList,
    addRegistration,
    updateRegistration,
    deleteRegistration,
  }
}

export default useCompanyRegistration
